import React, { useState } from 'react';
import { post } from './apiClient';
import { useNavigate } from 'react-router-dom';
import {
  Container, Card, CardHeader, CardContent,
  TextField, Box, Alert, CircularProgress, Button, Typography
} from '@mui/material';

// Keep in step with MAX_PRODUCT_CATEGORY_LENGTH in functions/config.py.
const MAX_CATEGORY_LENGTH = 75;

export default function CreateJobPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const categoryLength = productCategory.trim().length;
  const categoryOverLimit = categoryLength > MAX_CATEGORY_LENGTH;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await post('/jobs', { url, product_category: productCategory });
      navigate('/jobs'); 
    } catch (err) {
      try {
        const apiError = JSON.parse(err.message);
        setError(apiError.error || 'Failed to create job. Please check the inputs.');
      } catch (parseError) {
        console.error(parseError);
        setError('Failed to create job. An unknown error occurred.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Card>
        <CardHeader title="Create a New Query Generation Job" />
        <CardContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Website URL"
              type="url"
              name="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              required
              fullWidth
            />
            <TextField
              label="Product Category"
              type="text"
              name="product_category"
              value={productCategory}
              onChange={(e) => setProductCategory(e.target.value)}
              placeholder="e.g., enterprise paid media agency"
              error={categoryOverLimit}
              FormHelperTextProps={{ component: 'div' }}
              helperText={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption">
                    The category a buyer would search for — 2 to 5 words.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="caption">
                      {categoryLength} / {MAX_CATEGORY_LENGTH}
                    </Typography>
                    {categoryOverLimit && (
                      <Typography variant="caption" sx={{ color: 'error.main' }}>
                        {categoryLength - MAX_CATEGORY_LENGTH} characters over the limit
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'success.main' }}>
                    Good: enterprise paid media agency
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Too broad: marketing
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Too long: a description of your business or your buyer
                  </Typography>
                </Box>
              }
              required
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={loading || categoryOverLimit} sx={{ mt: 2 }}>
              {loading ? <CircularProgress size={24} /> : 'Submit Job'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
